import {
  Box,
  Stack,
  Skeleton,
  Alert,
  Typography,
  Divider,
  Chip,
} from '@mui/material';
import { useVariation } from '../../../hooks/useVariation';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { useVariationEventsTimeline } from '../../../hooks/useVariationEventsTimeline';
import { VariationStatusBadge } from '../atoms/VariationStatusBadge';
import { VariationImageGallery } from './VariationImageGallery';
import { PagedActivityTimeline } from '../molecules/PagedActivityTimeline';
import { formatCurrency } from '../../../../../shared/utils/currency';
import {
  formatDate,
  formatDateTime,
} from '../../../../../shared/utils/dateFormatting';
import { InfoRow } from '../atoms/InfoRow';

interface VariationDetailsPanelProps {
  variationId: string;
}

export function VariationDetailsPanel({
  variationId,
}: VariationDetailsPanelProps) {
  const { data: variation, error } = useVariation(variationId);
  const { siteId, plotId } = useSitePanelState();

  const siteIdNum = siteId
    ? parseInt(siteId, 10)
    : (variation?.plotContext.siteId ?? null);
  const plotIdNum = plotId
    ? parseInt(plotId, 10)
    : (variation?.plotContext.plotId ?? null);

  const {
    events,
    totalCount: timelineTotalCount,
    hasMore: timelineHasMore,
    isLoading: timelineLoading,
    isLoadingMore: timelineLoadingMore,
    loadMore: timelineLoadMore,
  } = useVariationEventsTimeline(siteIdNum, plotIdNum, variationId);

  if (error && !variation) {
    return (
      <Box data-testid="variation-details-panel" sx={{ p: 3 }}>
        <Alert severity="error">Failed to load variation details</Alert>
      </Box>
    );
  }

  if (!variation) {
    return (
      <Box data-testid="variation-details-panel" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={80} />
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={100} />
        </Stack>
      </Box>
    );
  }

  const { allocation } = variation;
  const workerLabel = allocation.worker.type === 'Gang' ? 'Gang' : 'Worker';
  const isGang = allocation.worker.type === 'Gang';

  return (
    <Box data-testid="variation-details-panel">
      <Box sx={{ p: 3, pb: 0 }}>
        <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
          {variation.workTypeName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flex: 1, minWidth: 0 }}
          >
            {variation.description}
          </Typography>
          <VariationStatusBadge status={variation.status} />
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Stack spacing={0}>
          {/* BOOKING section */}
          <Typography
            variant="overline"
            color="text.primary"
            sx={{ display: 'block', mb: 1, fontWeight: 600 }}
          >
            Booking
          </Typography>

          {variation.financialSummary !== null && (
            <InfoRow label="Amount" testId="financial-amount">
              {formatCurrency(variation.financialSummary.amount)}
            </InfoRow>
          )}

          <InfoRow label="Booked by" testId="booked-by">
            {variation.requestedBy}
          </InfoRow>

          <InfoRow label="Date" testId="booked-date">
            {formatDate(variation.bookedAt)}
          </InfoRow>

          <InfoRow label={workerLabel} testId="worker-name">
            {allocation.worker.displayName}
          </InfoRow>

          {isGang &&
            allocation.worker.memberNames &&
            allocation.worker.memberNames.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="member-names"
                sx={{ display: 'block', mb: 1.5, mt: -1 }}
              >
                {allocation.worker.memberNames.join(', ')}
              </Typography>
            )}

          {allocation.uniqueReference && (
            <InfoRow label="Reference" testId="unique-reference">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {allocation.uniqueReference}
                {allocation.hasPotSubmission && (
                  <Chip
                    label="Included in pot"
                    size="small"
                    color="info"
                    variant="outlined"
                    data-testid="pot-chip"
                    sx={{ height: 20, fontSize: '0.6875rem' }}
                  />
                )}
              </Box>
            </InfoRow>
          )}

          {variation.notes && (
            <InfoRow label="Notes" testId="notes" multiline>
              {variation.notes}
            </InfoRow>
          )}

          {/* LOCATION section */}
          <Divider sx={{ my: 2 }} />
          <Typography
            variant="overline"
            color="text.primary"
            sx={{ display: 'block', mb: 1, fontWeight: 600 }}
          >
            Location
          </Typography>
          <InfoRow label="Site" testId="location-site">
            {variation.plotContext.siteName}
          </InfoRow>
          <InfoRow label="Plot" testId="location-plot">
            {variation.plotContext.plotName}
          </InfoRow>
          <InfoRow label="Plot Number" testId="location-plot-number">
            {variation.plotContext.plotNumber}
          </InfoRow>

          {/* REVIEW section */}
          {variation.status !== 'Pending' && allocation.review && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography
                  variant="overline"
                  color="text.primary"
                  sx={{ display: 'block', fontWeight: 600 }}
                >
                  Review
                </Typography>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor:
                      variation.status === 'Approved'
                        ? 'success.main'
                        : 'error.main',
                    flexShrink: 0,
                  }}
                />
              </Box>
              <InfoRow label="Reviewed by" testId="reviewed-by">
                {allocation.review.reviewedBy}
              </InfoRow>
              <InfoRow label="Date" testId="reviewed-at">
                {formatDateTime(allocation.review.reviewedAt)}
              </InfoRow>
              {allocation.review.rejectionReason && (
                <InfoRow label="Reason" testId="rejection-reason" multiline>
                  {allocation.review.rejectionReason}
                </InfoRow>
              )}
              {allocation.review.rejectionNotes && (
                <InfoRow label="Notes" testId="rejection-notes" multiline>
                  {allocation.review.rejectionNotes}
                </InfoRow>
              )}
            </>
          )}

          {/* ACTIVITY section */}
          <Divider sx={{ my: 2 }} />
          <Typography
            variant="overline"
            color="text.primary"
            sx={{ display: 'block', mb: 1.5, fontWeight: 600 }}
          >
            Activity
          </Typography>
          <PagedActivityTimeline
            events={events}
            totalCount={timelineTotalCount}
            hasMore={timelineHasMore}
            isLoading={timelineLoading}
            isLoadingMore={timelineLoadingMore}
            loadMore={timelineLoadMore}
          />

          {/* PHOTOS section */}
          <Divider sx={{ my: 2 }} />
          <Typography
            variant="overline"
            color="text.primary"
            sx={{ display: 'block', mb: 1, fontWeight: 600 }}
          >
            Photos
          </Typography>
          <VariationImageGallery variationRequestId={variationId} minimal />
        </Stack>
      </Box>
    </Box>
  );
}
